import { createRoot } from "react-dom/client";
import App from "./App";
import { configureWebApiClient } from "./lib/api-config";
import { initDevLog } from "./lib/devlog-boot";
import { ObservabilityFallback } from "@/components/observability/observability-fallback";
import { isDesktopShell } from "@/lib/platform";
import { isPlayerSurface } from "@/lib/player-surface";
import { applyPersistedBrandingAtBoot } from "@/lib/workspace-branding-storage";
import { adoptFragmentCredential } from "@/player/lib/screen-credential";
import {
  ObservabilityErrorBoundary,
  initObservability,
  installGlobalErrorHandlers,
} from "@workspace/observability/web";
import { captureReferral } from "@/lib/referral";
import { persistReferralIfConsented } from "@/components/referral-claimer";
import "./index.css";

// Desktop hand-off: the shell opens/reloads the player window with the freshly
// claimed screen credential in the URL fragment. Adopt it BEFORE React renders
// so DeviceProvider + PlayerEntry both read the authoritative token — this is
// what makes "change device" re-point the reused player window instead of
// resuming the old device.
//
// Gated to the DESKTOP SHELL player surface. Requiring isDesktopShell() (the
// preload bridge) is a security boundary, not just a tidy-up: this same bundle
// is the public web player at omnimedia.app/player?surface=player, and the
// shell is the ONLY legitimate producer of a `#screen_credential=` fragment.
// Without the shell check, a crafted link could make any web visitor's browser
// persist and boot as an attacker-supplied device (session fixation). The web
// player pairs via QR/activate instead, never via a URL fragment.
if (isDesktopShell() && isPlayerSurface()) {
  adoptFragmentCredential();
}

configureWebApiClient();

// Paint the last-known workspace accent BEFORE React's first commit, so a
// branded tenant never flashes OmniMedia indigo on reload. This cannot live in
// a component: WorkspaceProvider withholds its children until GET /api/me
// resolves, so nothing inside React can apply an accent before the network
// answers. Self-cleans when the stored active-workspace id is gone or has
// changed, so a signed-out or switched-account browser gets nothing.
applyPersistedBrandingAtBoot();

// DEVLOGS-TEMP: dev debug-log pipeline. Placed AFTER adoptFragmentCredential()
// (which is a security boundary — the fragment carries the screen credential and
// must never be logged) and BEFORE initObservability(), so a failure inside
// Sentry/PostHog setup is itself captured. Fully inert without VITE_DEVLOG_URL.
initDevLog();

initObservability({
  platform: isDesktopShell() ? "desktop" : "web",
  sentryDsn: import.meta.env.VITE_SENTRY_DSN,
  posthogKey: import.meta.env.VITE_POSTHOG_KEY,
  posthogHost: import.meta.env.VITE_POSTHOG_HOST,
  release: import.meta.env.VITE_SENTRY_RELEASE,
  environment: import.meta.env.MODE,
});
installGlobalErrorHandlers();

// Partner / tracked-link attribution (`?ref=lee`, utm_*): read the params into
// memory before the router drops them; persisted only with analytics consent.
captureReferral(window.location.search);
persistReferralIfConsented();

createRoot(document.getElementById("root")!).render(
  <ObservabilityErrorBoundary
    fallback={({ resetError }) => (
      <ObservabilityFallback
        error={null}
        componentStack=""
        eventId=""
        resetError={resetError}
      />
    )}
    showDialog={false}
  >
    <App />
  </ObservabilityErrorBoundary>,
);
